<script setup lang="ts">
import { computed } from 'vue';
import {
  SEARCH_DOC_TYPE_FIRST_PUBLISHED_AT,
  searchPresetDates,
} from '@/constants';
import SearchMenuCheckbox from '@/components/menu/search-menu/search-menu-checkbox.vue';
import SearchMenuDropdownCalendar from '@/components/menu/search-menu/search-menu-dropdown-calendar.vue';
import SearchMenuEntertainment from '@/components/menu/search-menu/search-menu-entertainment.vue';
import { DateRange } from '@/types';
import { getToday } from '@/utils/dates';
import { SiteCategoryItems } from '@/utils/siteCategories';

interface Props {
  // メディア種別
  isMediaTypeVisible: boolean | undefined;
  isOpenMediaType: boolean;
  mediaTypeItems: { label: string; value: string }[];
  checkedMediaTypeItems: string[];

  // 期間
  isOpenDropDownCalendar: boolean;
  startDate: Date | undefined;
  endDate: Date | undefined;

  // サイト種別
  isOpenSiteCategories: boolean;
  siteCategoryItems: SiteCategoryItems[];
  checkedSiteCategoryItems: string[];

  // エンタメフィルタ
  isEntertainmentFilter: boolean | undefined;

  // 特許の公開庁
  isPatentPublicationOfficeVisible: boolean | undefined;
  isOpenPatentPublicationOffice: boolean;
  patentPublicationOfficeItems: { label: string; value: string }[];
  checkedPatentPublicationOfficeItems: string[];
}

// デフォルト値を設定しない場合は以下を使用してください。
const props = defineProps<Props>();

type Emits = {
  // メディア種別
  'open-media-type': [];
  'add-all-media-type': [];
  'remove-all-media-type': [];
  'add-media-type': [value: string];
  'remove-media-type': [value: string];

  // 期間
  'open-drop-down-calendar': [];
  'apply-date': [value: { presetKey: string; value: DateRange }];

  // サイト種別
  'open-site-categories': [];
  'remove-all-site-category': [];
  'add-site-category': [value: string];
  'remove-site-category': [value: string];

  // エンタメフィルタ
  'toggle-entertainment-filter': [];

  // 特許の公開庁
  'open-patent-publication-office': [];
  'add-all-patent-publication-office': [];
  'remove-all-patent-publication-office': [];
  'add-patent-publication-office': [value: string];
  'remove-patent-publication-office': [value: string];
};

const emit = defineEmits<Emits>();

const computedIsEntertainmentFilter = computed({
  get: () => props.isEntertainmentFilter,
  set: () => emit('toggle-entertainment-filter'),
});
</script>

<template>
  <div>
    <SearchMenuDropdownCalendar
      title="期間"
      :is-open="isOpenDropDownCalendar"
      :range="{
        from: startDate,
        to: endDate,
      }"
      :preset="searchPresetDates"
      :min="SEARCH_DOC_TYPE_FIRST_PUBLISHED_AT.all"
      :max="getToday()"
      @toggle-is-open="emit('open-drop-down-calendar')"
      @apply-date="value => emit('apply-date', value)"
    ></SearchMenuDropdownCalendar>
    <SearchMenuCheckbox
      v-if="isMediaTypeVisible"
      title="メディア種別"
      :items="mediaTypeItems"
      :checked-values="checkedMediaTypeItems"
      :is-open="isOpenMediaType"
      @toggle-is-open="emit('open-media-type')"
      @checked-all-item="emit('add-all-media-type')"
      @un-checked-all-item="emit('remove-all-media-type')"
      @checked-item="(item: string) => emit('add-media-type', item)"
      @un-checked-item="(item: string) => emit('remove-media-type', item)"
    ></SearchMenuCheckbox>
    <SearchMenuCheckbox
      title="サイト種別"
      :items="siteCategoryItems"
      :checked-values="checkedSiteCategoryItems"
      :is-open="isOpenSiteCategories"
      @toggle-is-open="emit('open-site-categories')"
      @un-checked-all-item="emit('remove-all-site-category')"
      @checked-item="(item: string) => emit('add-site-category', item)"
      @un-checked-item="(item: string) => emit('remove-site-category', item)"
    ></SearchMenuCheckbox>
    <SearchMenuEntertainment v-model="computedIsEntertainmentFilter" />
    <SearchMenuCheckbox
      v-if="isPatentPublicationOfficeVisible"
      title="公開庁"
      :items="patentPublicationOfficeItems"
      :checked-values="checkedPatentPublicationOfficeItems"
      :is-open="isOpenPatentPublicationOffice"
      @toggle-is-open="emit('open-patent-publication-office')"
      @checked-all-item="emit('add-all-patent-publication-office')"
      @un-checked-all-item="emit('remove-all-patent-publication-office')"
      @checked-item="
        (item: string) => emit('add-patent-publication-office', item)
      "
      @un-checked-item="
        (item: string) => emit('remove-patent-publication-office', item)
      "
    />
    <div class="divider" />
  </div>
</template>

<style lang="scss" scoped>
.divider {
  border-top: solid 1px $color-gray400;
}
</style>
